import { ChallengeService } from '@duck/api-definitions'
import { PlayIcon } from '@heroicons/react/24/outline'
import { MarkdownEditor } from '@duck/ui'
import { useTar, type TranslationKey } from '@/modules'
import { WeaponIcon } from '@components/WeaponIcon'
import { useOrgBranding } from '@/hooks/useOrgBranding'
import { getChallengeCover } from '@/utils/challengeCover.utils'
import {
  formatAssignmentDate,
  getAssignmentActionButton,
  getTimeLimitDisplay,
} from '@/utils/assignmentDisplay.utils'

const WEAPON_LABEL_KEY: Record<ChallengeService.TaskWeaponsOfChoiceEnum, TranslationKey> = {
  [ChallengeService.TaskWeaponsOfChoiceEnum.Sql]: 'common.sql',
  [ChallengeService.TaskWeaponsOfChoiceEnum.Python]: 'common.python',
}

interface AssignmentDetailProps {
  assignment: ChallengeService.AvailableAssignment
  onStart: () => void
}

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-center gap-2">
    <dt className="w-32 flex-none font-medium text-muted-foreground">{label}</dt>
    <dd className="text-foreground">{children}</dd>
  </div>
)

export const AssignmentDetail = ({ assignment, onStart }: AssignmentDetailProps) => {
  const { t, locale } = useTar()
  const { branding: organization } = useOrgBranding(assignment.organizationId)
  const cover = getChallengeCover({ taskId: assignment.taskId })
  const action = getAssignmentActionButton(assignment.status, assignment.startDate, assignment.endDate, t)
  const hasTimeLimit = assignment.timeLimit !== undefined && assignment.timeLimit !== null && assignment.timeLimit > 0

  return (
    <div className="flex h-full flex-col">
      {/* Cover: org logo on the colored gradient, matching the assignment card */}
      <div className="relative h-44 w-full flex-none">
        <div className="absolute inset-0" style={{ background: cover.gradient }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/55" />
        <div className="absolute inset-0 grid place-items-center p-4">
          {organization?.logoURL ? (
            <img
              src={organization.logoURL}
              alt={assignment.organizationName}
              className="max-h-16 max-w-[60%] object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.45)]"
            />
          ) : (
            <span className="line-clamp-2 text-center text-base font-bold text-white/90 drop-shadow">
              {assignment.organizationName}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <h2 className="mb-4 text-xl font-bold text-foreground">{assignment.name}</h2>
        <dl className="mb-6 flex flex-col gap-3 text-sm">
          <Row label={t('challenges.organizationLabel')}>{assignment.organizationName}</Row>
          {assignment.startDate ? (
            <Row label={t('challenges.startDateLabel')}>{formatAssignmentDate(assignment.startDate, locale)}</Row>
          ) : null}
          {assignment.endDate ? (
            <Row label={t('challenges.dueDateLabel')}>{formatAssignmentDate(assignment.endDate, locale)}</Row>
          ) : null}
          {assignment.overdueDate ? (
            <Row label={t('challenges.overdueDateLabel')}>{formatAssignmentDate(assignment.overdueDate, locale)}</Row>
          ) : null}
          {hasTimeLimit ? (
            <Row label={t('challenges.timeLimitLabel')}>{getTimeLimitDisplay(assignment.timeLimit ?? 0, t)}</Row>
          ) : null}
          <div className="flex items-center gap-2">
            <dt className="w-32 flex-none font-medium text-muted-foreground">{t('challenges.availableInLabel')}</dt>
            <dd className="flex flex-wrap items-center gap-3">
              {assignment.allowedWeapons.map((w) => (
                <span key={w} className="inline-flex items-center gap-1.5 text-foreground">
                  <WeaponIcon weapon={w} className="h-4 w-4 flex-none" />
                  {t(WEAPON_LABEL_KEY[w])}
                </span>
              ))}
            </dd>
          </div>
        </dl>

        {/* Starting-dialog content the partner set on the cohort. Nothing renders
            when it's empty (no fallback to the task description). MarkdownEditor is
            uncontrolled, so key it on the assignment to remount on navigation. */}
        {assignment.challengeDisclaimer !== undefined && assignment.challengeDisclaimer !== '' ? (
          <MarkdownEditor
            key={assignment.assignmentId}
            defaultValue={assignment.challengeDisclaimer}
            mode="view"
            noBorder
            className="max-h-[400px]"
            mdClassName="bg-transparent p-0"
          />
        ) : null}
      </div>

      <div className="flex flex-none gap-3 border-t border-border p-5">
        <button
          type="button"
          onClick={onStart}
          disabled={action.disabled}
          className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-3 font-bold text-white disabled:cursor-not-allowed disabled:opacity-60 ${action.className}`}
        >
          <PlayIcon className="w-4 h-4" />
          <span>{action.isCustomText ? action.text : t(action.text as TranslationKey)}</span>
        </button>
      </div>
    </div>
  )
}
